import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Button, Spinner, Form, Alert, Modal, Collapse } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import LoadingSpinner from './components/LoadingSpinner';
import PatientSummaryCard from './components/PatientSummaryCard';
import PatientHeader from './components/PatientHeader';
import JointDecisionsSpreadsheet from './components/JointDecisionsSpreadsheet';
import {
  savePatientNote,
  getPatientNotes,
  deletePatientNote,
  fetchRecommendationsNav,
  fetchRecommendationById,
  deactivateRecommendationsForAppointment
} from './api';
import MedicationCheckboxForm from './components/MedicationCheckboxForm';
import CompassWorkspace from './components/compass/CompassWorkspace';
import AllianceWorkspace from './components/alliance/AllianceWorkspace';
import { ENROLLMENT_STUDIES, studyConsentHandoff } from './studies/studyEligibility';
import './components/compass/compass.css';

const SYNTHETIC_SURGERY_APPOINTMENT_ID = '0';

function isSyntheticAppointmentId(appointmentId) {
  return String(appointmentId ?? '') === SYNTHETIC_SURGERY_APPOINTMENT_ID;
}

/** First real (non-"0") appointment id — the consult used to clone Med Management. */
function consultAppointmentIdFrom(...ids) {
  for (const id of ids) {
    if (id == null || id === '') continue;
    const value = String(id);
    if (value && !isSyntheticAppointmentId(value)) return value;
  }
  return undefined;
}

/** Appointment-chip row from a synthetic (appointment_id "0") recommendation. */
function navItemFromSyntheticRecommendation(rec, { label, date } = {}) {
  const parsed = rec?.parsed_output;
  const surg = parsed?.surgery;
  const first = Array.isArray(surg) && surg.length > 0 ? surg[0] : null;
  const proc = first?.procedure;
  let procedureLabel = label;
  if (!procedureLabel && proc) {
    procedureLabel = (Array.isArray(proc) ? proc.join(', ') : String(proc)).replace(/_/g, ' ');
  }
  return {
    recommendation_id: rec.recommendation_id,
    appointment_id: String(rec.appointment_id ?? SYNTHETIC_SURGERY_APPOINTMENT_ID),
    approval_status: rec.approval_status ?? null,
    procedure_label: procedureLabel || 'Surgery',
    reason_coding_code: rec.reason_coding_code,
    appointment_type: rec.appointment_type,
    surgery_start_time: rec.surgery_start_time || null,
    surgery_date: date || first?.date || rec.surgery_date || null,
  };
}

function upsertSyntheticNavItem(items, rec, meta) {
  const next = navItemFromSyntheticRecommendation(rec, meta);
  const rest = (Array.isArray(items) ? items : []).filter(
    (item) => String(item?.appointment_id ?? '') !== SYNTHETIC_SURGERY_APPOINTMENT_ID
  );
  return [next, ...rest];
}

/** Newest by surgery OPM time / JSON surgery date; ties stay stable. */
function getLatestInactiveNavItem(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const score = (item) => {
    const s = item?.surgery_start_time || item?.surgery_date;
    if (!s) return 0;
    const t = new Date(s).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  return [...items].sort((a, b) => score(b) - score(a))[0];
}

function pickDefaultActiveRecId(patient, activeItems) {
  if (!Array.isArray(activeItems) || activeItems.length === 0) return null;
  const pAid = patient?.appointment_id;
  if (pAid != null && pAid !== '') {
    const s = String(pAid);
    const match = activeItems.find(
      (it) => it.appointment_id != null && String(it.appointment_id) === s
    );
    if (match?.recommendation_id != null) return match.recommendation_id;
  }
  return activeItems[0].recommendation_id ?? null;
}

function PatientTab({
  patient,
  onBack,
  onPatientApproval,
  user,
  showSurgeryOnly = false,
  onJointDecisionsEngage,
  onGoToMedManagement,
  onDecideComplete,
  onOfferStudyConsent,
  blurPatientNames = false,
  showCompass = false,
  study = '',
  studyHandoff = null,
  studyWithdrawn = false,
  withdrawBusy = false,
  withdrawError = '',
  onWithdrawFromStudy = null,
  enrollmentSurgeryPrompt = 0,
}) {
  const [periopResult, setPeriopResult] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [isUpdatingRecommendation, setIsUpdatingRecommendation] = useState(true);
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [messages, setMessages] = useState([]);
  const [refreshMessages, setRefreshMessages] = useState(0);
  const [resetFormTrigger, setResetFormTrigger] = useState(0);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [recommendationNavItems, setRecommendationNavItems] = useState([]);
  const [inactiveNavItems, setInactiveNavItems] = useState([]);
  /** Active + inactive appointment chip lists (separate from main recommendation fetch) */
  const [appointmentNavLoading, setAppointmentNavLoading] = useState(false);
  /** While switching appointments, highlight the clicked chip before periopResult updates */
  const [navPendingRecommendationId, setNavPendingRecommendationId] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTargetAppointmentId, setDeactivateTargetAppointmentId] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  /** Active appointment chips: collapsible; default expanded */
  const [activeAppointmentsExpanded, setActiveAppointmentsExpanded] = useState(true);
  /** Past/inactive appointment chips: hidden by default; expand to navigate */
  const [inactivePastExpanded, setInactivePastExpanded] = useState(false);
  /** Hide appointment picker entirely while actively using Joint Decisions */
  const [hideAppointmentsForJointDecisions, setHideAppointmentsForJointDecisions] = useState(false);
  const prevArchiveViewRef = useRef(false);
  const activeDefaultLoadInFlightRef = useRef(false);
  const inactiveDefaultLoadInFlightRef = useRef(false);
  const medicationFormRef = useRef();
  const listRowContextRef = useRef(null);
  const periopResultRef = useRef(null);
  /** Keep Med Management mounted after first visit so JD ↔ MM does not reload it. */
  const [medManagementMounted, setMedManagementMounted] = useState(showSurgeryOnly);
  /** Keep Joint Decisions mounted after first visit; hide it on the Med Management tab. */
  const [jointDecisionsMounted, setJointDecisionsMounted] = useState(!showSurgeryOnly);
  const [jdStageReady, setJdStageReady] = useState(false);
  const [approvalFeedback, setApprovalFeedback] = useState(null);

  /** OPM appointment this patient's periop row is tied to (required for API). Prefer loaded recommendation. */
  const appointmentId = (() => {
    const pick = (v) => {
      if (v == null || v === '') return undefined;
      return typeof v === 'string' ? v : String(v);
    };
    return pick(periopResult?.appointment_id) ?? pick(patient?.appointment_id);
  })();

  const formatRecommendationNavLabel = (item) => {
    const proc = item?.procedure_label || item?.reason_coding_code || 'Surgery';
    const dtStr = item?.surgery_start_time || item?.surgery_date;
    if (!dtStr) return proc;
    const d = new Date(dtStr);
    if (Number.isNaN(d.getTime())) return proc;
    return `${proc} — ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  /** Procedure + compact one-line date for dense chips */
  const formatNavChipOneLine = (item) => {
    const proc = item?.procedure_label || item?.reason_coding_code || 'Surgery';
    const dtStr = item?.surgery_start_time || item?.surgery_date;
    let datePart = null;
    if (dtStr) {
      const d = new Date(dtStr);
      if (!Number.isNaN(d.getTime())) {
        datePart = d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
    return { proc, datePart };
  };

  /** Merge live periop approval when viewing that recommendation (keeps chips in sync without refetch). */
  const approvalStatusForNavItem = (item) => {
    const rid = item?.recommendation_id;
    if (
      rid != null &&
      periopResult?.recommendation_id != null &&
      String(rid) === String(periopResult.recommendation_id)
    ) {
      return periopResult.approval_status ?? item?.approval_status;
    }
    return item?.approval_status;
  };

  const getAppointmentNavChipStyle = (approvalStatus, selected, isUpdating) => {
    const base = {
      flex: '0 1 auto',
      minWidth: 0,
      cursor: isUpdating ? 'wait' : 'pointer',
      borderStyle: 'solid',
      lineHeight: 1.25,
      whiteSpace: 'normal',
      fontSize: '0.8125rem',
    };
    const borderW = selected ? 3 : 1;
    const dimOthers = isUpdating && !selected;
    if (approvalStatus === 'approved') {
      const edge = selected ? 'var(--action-approve)' : 'rgba(92, 153, 110, 0.5)';
      return {
        ...base,
        borderWidth: borderW,
        borderColor: edge,
        backgroundColor: selected ? 'rgba(92, 153, 110, 0.2)' : 'rgba(92, 153, 110, 0.08)',
        boxShadow: selected
          ? '0 0 0 1px rgba(92, 153, 110, 0.45), 0 1px 6px rgba(92, 153, 110, 0.35)'
          : 'none',
        opacity: dimOthers ? 0.65 : 1,
      };
    }
    if (approvalStatus === 'denied') {
      const edge = selected ? 'var(--action-deny)' : 'rgba(220, 53, 69, 0.55)';
      return {
        ...base,
        borderWidth: borderW,
        borderColor: edge,
        backgroundColor: selected ? 'rgba(220, 53, 69, 0.14)' : 'rgba(220, 53, 69, 0.06)',
        boxShadow: selected
          ? '0 0 0 1px rgba(220, 53, 69, 0.45), 0 1px 6px rgba(220, 53, 69, 0.25)'
          : 'none',
        opacity: dimOthers ? 0.65 : 1,
      };
    }
    const edge = selected ? 'var(--brand-primary-medium)' : 'rgba(0, 0, 0, 0.12)';
    return {
      ...base,
      borderWidth: borderW,
      borderColor: edge,
      backgroundColor: selected ? 'var(--brand-primary)' : '#fff',
      boxShadow: selected
        ? '0 0 0 1px rgba(32, 92, 143, 0.35), 0 1px 4px rgba(32, 92, 143, 0.2)'
        : 'none',
      opacity: dimOthers ? 0.65 : 1,
    };
  };

  const navChipTitleTextColor = (approvalStatus) => {
    if (approvalStatus === 'approved') return '#1a4d22';
    if (approvalStatus === 'denied') return '#842029';
    return 'var(--brand-primary-dark)';
  };

  const handleRecommendationNavChange = async (recommendationId) => {
    if (!patient?.id || !recommendationId) {
      return;
    }
    if (
      recommendationId === periopResult?.recommendation_id &&
      periopResult?.active !== false
    ) {
      return;
    }
    setNavPendingRecommendationId(String(recommendationId));
    setIsUpdatingRecommendation(true);
    setSaveError(null);
    try {
      const rec = await fetchRecommendationById(patient.id, recommendationId);
      if (rec) {
        setPeriopResult(rec);
        setRefreshMessages((n) => n + 1);
      }
    } catch (error) {
      console.error('Failed to load recommendation:', error);
      setSaveError('Failed to load that recommendation.');
    } finally {
      setNavPendingRecommendationId(null);
      setIsUpdatingRecommendation(false);
    }
  };

  const handleDeactivateAppointmentConfirm = async () => {
    const aid = deactivateTargetAppointmentId ?? appointmentId;
    if (!patient?.id || !aid) {
      setShowDeactivateModal(false);
      setDeactivateTargetAppointmentId(null);
      return;
    }
    setIsDeactivating(true);
    setSaveError(null);
    try {
      await deactivateRecommendationsForAppointment(patient.id, aid);
      setAppointmentNavLoading(true);
      let activeItems;
      let inactiveItems;
      try {
        ({ active: activeItems, inactive: inactiveItems } = await fetchRecommendationsNav(patient.id));
        setRecommendationNavItems(activeItems || []);
        setInactiveNavItems(inactiveItems || []);
      } finally {
        setAppointmentNavLoading(false);
      }
      setShowDeactivateModal(false);
      setDeactivateTargetAppointmentId(null);
      setNavPendingRecommendationId(null);
      const next = (activeItems || [])[0];
      if (next?.recommendation_id) {
        setIsUpdatingRecommendation(true);
        try {
          const rec = await fetchRecommendationById(patient.id, next.recommendation_id);
          if (rec) setPeriopResult(rec);
          else setPeriopResult(null);
        } catch (e) {
          console.error(e);
          setPeriopResult(null);
        } finally {
          setIsUpdatingRecommendation(false);
        }
      } else {
        setPeriopResult(null);
      }
      setRefreshMessages((n) => n + 1);
    } catch (error) {
      console.error('Deactivate appointment failed:', error);
      setSaveError(error?.message || 'Failed to deactivate recommendations.');
      setShowDeactivateModal(false);
      setDeactivateTargetAppointmentId(null);
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleInactiveRecommendationNavChange = async (recommendationId) => {
    if (!patient?.id || !recommendationId) {
      return;
    }
    if (
      recommendationId === periopResult?.recommendation_id &&
      periopResult?.active === false
    ) {
      return;
    }
    setNavPendingRecommendationId(String(recommendationId));
    setIsUpdatingRecommendation(true);
    setSaveError(null);
    try {
      const rec = await fetchRecommendationById(patient.id, recommendationId, { inactive: true });
      if (rec) {
        setPeriopResult(rec);
        setRefreshMessages((n) => n + 1);
      }
    } catch (error) {
      console.error('Failed to load inactive recommendation:', error);
      setSaveError('Failed to load that past appointment.');
    } finally {
      setNavPendingRecommendationId(null);
      setIsUpdatingRecommendation(false);
    }
  };

  useEffect(() => {
    if (showSurgeryOnly) {
      setMedManagementMounted(true);
    } else {
      setJointDecisionsMounted(true);
    }
  }, [showSurgeryOnly]);

  useEffect(() => {
    if (!showSurgeryOnly) return undefined;
    const scrollMedManagementToTop = () => {
      const container = document.getElementById('patient-details-container');
      const active = document.activeElement;
      if (active && container?.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }
      if (container) {
        container.scrollTop = 0;
        container.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
      }
    };
    scrollMedManagementToTop();
    const frame = window.requestAnimationFrame(() => {
      scrollMedManagementToTop();
      window.requestAnimationFrame(scrollMedManagementToTop);
    });
    const t0 = window.setTimeout(scrollMedManagementToTop, 0);
    const t1 = window.setTimeout(scrollMedManagementToTop, 50);
    const t2 = window.setTimeout(scrollMedManagementToTop, 250);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [showSurgeryOnly]);

  // Add useEffect to track pending changes from the form
  useEffect(() => {
    if (!showSurgeryOnly) {
      setHasPendingChanges(false);
      return;
    }
    const checkPendingChanges = () => {
      if (medicationFormRef.current) {
        setHasPendingChanges(medicationFormRef.current.hasPendingChanges || false);
      }
    };

    // Check immediately and then set up interval
    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 100);

    return () => clearInterval(interval);
  }, [showSurgeryOnly]);

  // Move generatePatientText inside the component and memoize it
  const generatePatientText = useCallback(() => {
    const patientData = {
      conditions: patient?.conditions?.map(c => `${c.key}: ${c.rationale}`).join('\n'),
      medications: patient?.medications?.map(m => `${m.key}: ${m.rationale}`).join('\n'),
      risks: patient?.risks?.map(r => `${r.key}: ${r.rationale}`).join('\n'),
      events: patient?.events?.map(e => `${e.key}: ${e.rationale}`).join('\n'),
      summary: patient?.summary?.content || '',
    };

    return Object.entries(patientData)
      .filter(([_, value]) => value && value.trim())
      .map(([key, value]) => `${key.toUpperCase()}:\n${value}`)
      .join('\n\n');
  }, [
    patient?.conditions,
    patient?.medications,
    patient?.risks,
    patient?.events,
    patient?.summary?.content
  ]);

  /** After note save/delete: refresh nav chips, refetch full recommendation (POST bodies can be thin), re-render. */
  const refreshUIFromNoteResponse = useCallback(
    async (data) => {
      const patientId = patient?.id || patient?.emr_source?.patient_id;
      if (!patientId) return;
      const { active: activeItems, inactive: inactiveItems } = await fetchRecommendationsNav(
        patientId
      );
      setRecommendationNavItems(activeItems || []);
      setInactiveNavItems(inactiveItems || []);

      const rec = data?.recommendation;
      if (rec?.recommendation_id) {
        try {
          const fresh = await fetchRecommendationById(patientId, String(rec.recommendation_id), {
            inactive: rec?.active === false
          });
          if (fresh) {
            setPeriopResult(fresh);
            setResetFormTrigger((p) => p + 1);
            setRefreshMessages((p) => p + 1);
            return;
          }
        } catch (e) {
          console.error('Refetch recommendation after note change failed:', e);
        }
      }
      if (rec) {
        setPeriopResult(rec);
      }
      setResetFormTrigger((p) => p + 1);
      setRefreshMessages((p) => p + 1);
    },
    [patient?.id, patient?.emr_source?.patient_id]
  );

  const handleMedicationFormSave = ({ added, removed, additionalInfo }) => {
    // Use these arrays for saving
    const patientId = patient?.id || patient?.emr_source?.patient_id;
    if (!patientId) {
      setSaveError('Cannot save note: Patient ID is missing');
      return;
    }
    if (!appointmentId) {
      setSaveError('Cannot save note: no appointment is linked to this patient. Refresh or contact support.');
      return;
    }
    const surgeryInAdded = added.filter((a) => a.scheduled_surgery);
    const surgeryInRemoved = removed.filter((r) => r.scheduled_surgery);
    const charAdded = added.filter((a) => !a.scheduled_surgery);
    const charRemoved = removed.filter((r) => !r.scheduled_surgery);
    const additionalSurgery =
      surgeryInAdded.length > 0 ? surgeryInAdded[surgeryInAdded.length - 1] : undefined;
    const clearAdditionalSurgery =
      surgeryInRemoved.length > 0 && surgeryInAdded.length === 0;

    if (
      (additionalInfo && additionalInfo.trim()) ||
      charAdded.length > 0 ||
      charRemoved.length > 0 ||
      additionalSurgery ||
      clearAdditionalSurgery
    ) {
      (async () => {
        try {
          setIsUpdatingRecommendation(true);
          setSaveError(null);
          const data = await savePatientNote(
            patientId,
            additionalInfo ? additionalInfo.trim() : '',
            charAdded,
            charRemoved,
            appointmentId,
            { additionalSurgery, clearAdditionalSurgery }
          );
          if (data.note_id) {
            setSavedNotes([
              {
                note_id: data.note_id,
                note_text: additionalInfo ? additionalInfo.trim() : '',
                created_at: data.created_at,
                user_email: user?.email || 'Unknown User',
                added: charAdded,
                removed: charRemoved
              },
              ...savedNotes
            ]);
          }
          if (additionalSurgery) {
            window.location.reload();
            return;
          }
          await refreshUIFromNoteResponse(data);
        } catch (error) {
          setSaveError(error.message || 'Failed to save note');
        } finally {
          setIsUpdatingRecommendation(false);
        }
      })();
    } else {
      setSaveError(
        'Add note text or change a medication, event, condition, risk, or surgery before saving.'
      );
    }
  };

  const handleDecideSubmit = async ({
    periopToken,
    date,
    label,
    added = [],
    removed = [],
    replaceSynthetic = false,
    sourceAppointmentId,
    characteristics = null,
    goToEnrollment = false,
  } = {}) => {
    const patientId = patient?.id || patient?.emr_source?.patient_id;
    if (!patientId) {
      throw new Error('Cannot create recommendation: Patient ID is missing.');
    }
    const consultId = consultAppointmentIdFrom(sourceAppointmentId, patient?.appointment_id);
    const noteAppointmentId = replaceSynthetic
      ? (consultId || appointmentId)
      : appointmentId;
    if (!noteAppointmentId) {
      throw new Error('Cannot create recommendation: no appointment is linked to this patient.');
    }
    if (!periopToken || !date) {
      throw new Error('Select a surgery and date.');
    }
    const additionalSurgery = {
      scheduled_surgery: {
        procedure: [periopToken],
        date,
      },
      valid: true,
    };
    const toNoteMed = (item) => {
      if (typeof item === 'string') {
        return item ? { medication: item, valid: true } : null;
      }
      const medication = item?.token || item?.medication;
      if (!medication) return null;
      const dosage = String(item?.dosage ?? '').trim();
      return {
        medication,
        valid: true,
        ...(dosage ? { dosage } : {}),
      };
    };
    const addedMeds = (Array.isArray(added) ? added : []).map(toNoteMed).filter(Boolean);
    const removedMeds = (Array.isArray(removed) ? removed : []).map(toNoteMed).filter(Boolean);
    setIsUpdatingRecommendation(true);
    setMedManagementMounted(true);
    setHideAppointmentsForJointDecisions(false);
    setSaveError(null);
    const handoff = studyConsentHandoff({
      procedureKey: periopToken,
      procedureLabel: label,
      surgeryDate: date,
      characteristics,
    });
    const openEnrollment = Boolean(goToEnrollment);
    if (!openEnrollment) onGoToMedManagement?.();
    onOfferStudyConsent?.(handoff);
    try {
      // Deactivate the existing add-on MM rec before cloning from the consult, same as
      // a fresh Decide. Skip the extra deactivate when the only source is "0" so we can
      // still load that row as the clone base.
      if (replaceSynthetic && consultId) {
        await deactivateRecommendationsForAppointment(
          patientId,
          SYNTHETIC_SURGERY_APPOINTMENT_ID,
        );
      }
      const data = await savePatientNote(
        patientId,
        '',
        addedMeds,
        removedMeds,
        noteAppointmentId,
        { additionalSurgery }
      );
      if (data?.note_id) {
        setSavedNotes((prev) => [
          {
            note_id: data.note_id,
            note_text: '',
            created_at: data.created_at,
            user_email: user?.email || 'Unknown User',
            added: addedMeds,
            removed: removedMeds,
          },
          ...prev,
        ]);
      }
      const rec = data?.synthetic_recommendation;
      if (rec?.recommendation_id) {
        if (replaceSynthetic) {
          const previousSynthetic = (recommendationNavItems || []).find(
            (item) => String(item?.appointment_id ?? '') === SYNTHETIC_SURGERY_APPOINTMENT_ID
          );
          if (previousSynthetic) {
            setInactiveNavItems((inactivePrev) => [
              previousSynthetic,
              ...(Array.isArray(inactivePrev) ? inactivePrev : []).filter(
                (item) => String(item?.appointment_id ?? '') !== SYNTHETIC_SURGERY_APPOINTMENT_ID
              ),
            ]);
          }
        }
        setRecommendationNavItems((prev) =>
          upsertSyntheticNavItem(prev, rec, { label, date })
        );
        setPeriopResult(rec);
        setResetFormTrigger((p) => p + 1);
        setRefreshMessages((n) => n + 1);
        onDecideComplete?.(patientId, SYNTHETIC_SURGERY_APPOINTMENT_ID);
        return;
      }
      const { active: activeItems, inactive: inactiveItems } = await fetchRecommendationsNav(patientId);
      const synthetic = (activeItems || []).find(
        (item) => String(item?.appointment_id ?? '') === SYNTHETIC_SURGERY_APPOINTMENT_ID
      );
      if (!synthetic?.recommendation_id) {
        throw new Error('Created the surgery appointment, but could not load its recommendation.');
      }
      const fetched = await fetchRecommendationById(patientId, synthetic.recommendation_id);
      if (!fetched) {
        throw new Error('Created the surgery appointment, but could not load its recommendation.');
      }
      setRecommendationNavItems(activeItems || []);
      setInactiveNavItems(inactiveItems || []);
      setPeriopResult(fetched);
      setResetFormTrigger((p) => p + 1);
      setRefreshMessages((n) => n + 1);
      onDecideComplete?.(patientId, SYNTHETIC_SURGERY_APPOINTMENT_ID);
    } catch (error) {
      const message = error?.message || 'Failed to create recommendation.';
      setSaveError(message);
      throw error instanceof Error ? error : new Error(message);
    } finally {
      setIsUpdatingRecommendation(false);
    }
  };

  periopResultRef.current = periopResult;

  useEffect(() => {
    setJdStageReady(false);
  }, [patient?.id]);

  // Clear prior recommendation when the list row changes (same person can have MM + JD).
  // Keep the rec if Decide just loaded appointment "0" and the list row then catches up.
  useEffect(() => {
    if (!patient?.id) {
      return;
    }
    const key = `${patient.id}::${patient.appointment_id ?? ''}`;
    const prevKey = listRowContextRef.current;
    listRowContextRef.current = key;
    if (prevKey === key) return;

    const prevPid = prevKey ? prevKey.split('::')[0] : null;
    const aid = String(patient.appointment_id ?? '');
    const rec = periopResultRef.current;
    if (prevPid === String(patient.id) && rec && String(rec.appointment_id ?? '') === aid) {
      return;
    }

    setPeriopResult(null);
    setIsUpdatingRecommendation(true);
  }, [patient?.id, patient?.appointment_id]);

  // Load an existing active recommendation from nav (no /periop/schedule/recommend generation).
  useEffect(() => {
    if (!patient?.id || appointmentNavLoading) return;
    if (recommendationNavItems.length === 0) return;

    const inActiveNavList = (id) =>
      recommendationNavItems.some(
        (it) => it?.recommendation_id != null && String(it.recommendation_id) === id
      );
    if (periopResult) {
      // If any recommendation is already loaded (active or inactive), preserve it.
      // This prevents automatic fallback to the current active appointment after a
      // user explicitly selects a past/inactive appointment.
      // Clear loading if a previous in-flight fetch was cancelled; otherwise the Save Note
      // button stays disabled (finally skipped set false when `cancelled`).
      setIsUpdatingRecommendation(false);
      return;
    }

    const defaultRid = pickDefaultActiveRecId(patient, recommendationNavItems);
    if (defaultRid == null) {
      setIsUpdatingRecommendation(false);
      return;
    }
    if (activeDefaultLoadInFlightRef.current) return;

    let cancelled = false;
    activeDefaultLoadInFlightRef.current = true;
    setIsUpdatingRecommendation(true);
    setSaveError(null);
    fetchRecommendationById(patient.id, defaultRid)
      .then((rec) => {
        if (cancelled) return;
        if (rec) {
          setPeriopResult(rec);
          setRefreshMessages((n) => n + 1);
        } else {
          setPeriopResult(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load active recommendation:', error);
          setSaveError('Failed to load recommendation.');
          setPeriopResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsUpdatingRecommendation(false);
        activeDefaultLoadInFlightRef.current = false;
      });
    return () => {
      cancelled = true;
      activeDefaultLoadInFlightRef.current = false;
    };
  }, [
    patient?.id,
    patient?.appointment_id,
    appointmentNavLoading,
    recommendationNavItems,
    periopResult?.recommendation_id,
    periopResult?.active,
  ]);

  // No recommendations in nav for this patient (e.g. empty after load)
  useEffect(() => {
    if (!patient?.id || appointmentNavLoading) return;
    if (recommendationNavItems.length > 0 || inactiveNavItems.length > 0) return;
    setPeriopResult(null);
    setIsUpdatingRecommendation(false);
  }, [
    patient?.id,
    appointmentNavLoading,
    recommendationNavItems.length,
    inactiveNavItems.length,
  ]);

  useEffect(() => {
    if (!patient?.id) {
      setRecommendationNavItems([]);
      setInactiveNavItems([]);
      setAppointmentNavLoading(false);
      return;
    }
    let cancelled = false;
    setRecommendationNavItems([]);
    setInactiveNavItems([]);
    setAppointmentNavLoading(true);
    fetchRecommendationsNav(patient.id)
      .then(({ active: activeItems, inactive: inactiveItems }) => {
        if (!cancelled) {
          setRecommendationNavItems(activeItems || []);
          setInactiveNavItems(inactiveItems || []);
        }
      })
      .catch((err) => {
        console.error('Failed to load appointment navigation:', err);
        if (!cancelled) {
          setRecommendationNavItems([]);
          setInactiveNavItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAppointmentNavLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  // When the patient has no active recommendations, load the most recent inactive (archive).
  useEffect(() => {
    if (!patient?.id || appointmentNavLoading) return;
    if (recommendationNavItems.length > 0 || inactiveNavItems.length === 0) return;

    const inInactiveList = (id) =>
      inactiveNavItems.some(
        (it) => it?.recommendation_id != null && String(it.recommendation_id) === id
      );
    if (
      periopResult?.active === false &&
      inInactiveList(String(periopResult?.recommendation_id ?? ''))
    ) {
      setIsUpdatingRecommendation(false);
      return;
    }
    if (inactiveDefaultLoadInFlightRef.current) return;

    const latest = getLatestInactiveNavItem(inactiveNavItems);
    const rid = latest?.recommendation_id;
    if (rid == null) return;

    let cancelled = false;
    inactiveDefaultLoadInFlightRef.current = true;
    setIsUpdatingRecommendation(true);
    setSaveError(null);
    fetchRecommendationById(patient.id, rid, { inactive: true })
      .then((rec) => {
        if (cancelled) return;
        if (rec) {
          setPeriopResult(rec);
          setRefreshMessages((n) => n + 1);
        }
        setInactivePastExpanded(true);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load default inactive recommendation:', error);
          setSaveError('Failed to load past appointment.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsUpdatingRecommendation(false);
        inactiveDefaultLoadInFlightRef.current = false;
      });
    return () => {
      cancelled = true;
      inactiveDefaultLoadInFlightRef.current = false;
    };
  }, [
    patient?.id,
    appointmentNavLoading,
    recommendationNavItems,
    inactiveNavItems,
    periopResult?.recommendation_id,
    periopResult?.active,
  ]);

  useEffect(() => {
    setNavPendingRecommendationId(null);
  }, [patient?.id]);

  useEffect(() => {
    setActiveAppointmentsExpanded(true);
    setInactivePastExpanded(false);
    setHideAppointmentsForJointDecisions(false);
    setApprovalFeedback(null);
  }, [patient?.id]);

  useEffect(() => {
    const isArchive = periopResult?.active === false;
    if (prevArchiveViewRef.current && !isArchive) {
      setInactivePastExpanded(false);
    }
    prevArchiveViewRef.current = isArchive;
  }, [periopResult?.active, periopResult?.recommendation_id]);

  // Load notes when patient changes
  useEffect(() => {
    const loadNotes = async () => {
      if (patient?.id) {
        try {
          const notes = await getPatientNotes(patient.id);
          setSavedNotes(notes || []);
        } catch (error) {
          setSaveError('Failed to load notes');
          setSavedNotes([]);
        } 
      }
    };
    loadNotes();
  }, [patient?.id]);

  if (!patient) {
    return <LoadingSpinner />;
  }

  const handleRecommendationApproval = ({ status, note, appointmentId: approvedAppointmentId } = {}) => {
    if (!status) return;
    const now = new Date().toISOString();
    const recId = periopResult?.recommendation_id;
    const aid =
      approvedAppointmentId != null && approvedAppointmentId !== ''
        ? String(approvedAppointmentId)
        : appointmentId != null
          ? String(appointmentId)
          : null;

    setPeriopResult((prev) =>
      prev
        ? {
            ...prev,
            approval_status: status,
            approval_time: now,
            approval_note: note || prev.approval_note || null,
          }
        : prev
    );
    setRecommendationNavItems((prev) =>
      (prev || []).map((item) => {
        const sameRec =
          recId != null &&
          item?.recommendation_id != null &&
          String(item.recommendation_id) === String(recId);
        const sameAppt =
          aid != null &&
          item?.appointment_id != null &&
          String(item.appointment_id) === aid;
        if (!sameRec && !sameAppt) return item;
        return { ...item, approval_status: status };
      })
    );
    setRefreshMessages((n) => n + 1);
    setApprovalFeedback({ status, at: Date.now() });
    const patientId = patient?.id;
    if (patientId) {
      onPatientApproval?.(patientId, aid, status);
    }
  };

  const handleSelectNote = (noteId) => {
    const newSelected = new Set(selectedNotes);
    if (newSelected.has(noteId)) {
      newSelected.delete(noteId);
    } else {
      newSelected.add(noteId);
    }
    setSelectedNotes(newSelected);
  };

  const handleDeleteSelected = async () => {
    const patientId = patient?.id || patient?.emr_source?.patient_id;
    
    if (!patientId) {
      setSaveError('Cannot delete notes: Patient ID is missing');
      return;
    }

    try {
      setSavedNotes(savedNotes.filter(note => !selectedNotes.has(note.note_id)));
      setIsUpdatingRecommendation(true);
      
      if (!appointmentId) {
        setSaveError('Cannot delete notes: no appointment is linked to this patient.');
        setIsUpdatingRecommendation(false);
        return;
      }
      const patientText = generatePatientText();
      let lastResult;
      const noteIds = Array.from(selectedNotes);
      for (let i = 0; i < noteIds.length; i++) {
        const noteId = noteIds[i];
        const isLastNote = i === noteIds.length - 1;
        lastResult = await deletePatientNote(patientId, noteId, patientText, isLastNote, appointmentId);
      }
      
      setSelectedNotes(new Set());
      if (lastResult) {
        await refreshUIFromNoteResponse(lastResult);
      } else {
        setRefreshMessages((prev) => prev + 1);
      }
    } catch (error) {
      const response = await getPatientNotes(patientId);
      setSavedNotes(response || []);
      setSaveError('Failed to delete notes');
    } finally {
      setIsUpdatingRecommendation(false);
    }
  };

  const handleMedicationFormSubmit = (noteText) => {
    setAdditionalInfo(prevInfo => {
      // If there's existing text, add a newline
      const prefix = prevInfo.trim() ? prevInfo.trim() + '\n' : '';
      return prefix + noteText;
    });
  };

  const handleJointDecisionsEngage = () => {
    window.setTimeout(() => {
      setActiveAppointmentsExpanded(false);
      setInactivePastExpanded(false);
      setHideAppointmentsForJointDecisions(true);
      onJointDecisionsEngage?.();
    }, 0);
  };

  const handleChartEngage = () => {
    handleJointDecisionsEngage();
  };

  const handleDeleteNote = async (noteId) => {
    const patientId = patient?.id || patient?.emr_source?.patient_id;
    if (!patientId || !appointmentId) {
      setSaveError('Cannot delete note: missing patient or appointment.');
      return;
    }
    try {
      setIsUpdatingRecommendation(true);
      const patientText = generatePatientText();
      const isLastNote = savedNotes.length === 1;
      const data = await deletePatientNote(patientId, noteId, patientText, isLastNote, appointmentId);
      
      // Update notes list
      setSavedNotes(savedNotes.filter(note => note.note_id !== noteId));
      await refreshUIFromNoteResponse(data);
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setIsUpdatingRecommendation(false);
    }
  };

  // Save button should only be disabled if nothing to save
  

  const isArchiveView = periopResult?.active === false;
  const showInactivePastChips = inactivePastExpanded;

  const appointmentScheduleSlot = (
    <>
      {appointmentNavLoading && (
        <div
          className="mb-2 d-flex align-items-center gap-2 text-muted small"
          role="status"
          aria-live="polite"
        >
          <Spinner animation="border" size="sm" />
          <span>Loading patient&apos;s schedule…</span>
        </div>
      )}
      {!appointmentNavLoading &&
        (recommendationNavItems.length > 0 || inactiveNavItems.length > 0) && (
        <div className="mb-2 recommendation-appointment-picker">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {recommendationNavItems.length > 0 && (
              <button
                type="button"
                className="btn btn-link text-start text-decoration-none p-0 mb-0 d-flex align-items-center gap-2"
                onClick={() => setActiveAppointmentsExpanded((e) => !e)}
                aria-expanded={activeAppointmentsExpanded}
                aria-controls="active-appointments-chips"
                id="active-appointments-toggle"
              >
                {activeAppointmentsExpanded ? (
                  <ChevronDown size={14} className="flex-shrink-0" aria-hidden />
                ) : (
                  <ChevronRight size={14} className="flex-shrink-0" aria-hidden />
                )}
                <span
                  className="badge rounded-pill px-2 py-1 border small"
                  style={{
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-primary-dark)',
                    borderColor: 'var(--brand-primary-medium)',
                  }}
                >
                  {recommendationNavItems.length}
                </span>
                <span
                  className="fw-semibold small"
                  style={{ color: 'var(--brand-primary-dark)' }}
                >
                  active appointments
                </span>
              </button>
            )}
            {inactiveNavItems.length > 0 && (
              <button
                type="button"
                className="btn btn-link text-start text-secondary text-decoration-none p-0 mb-0 d-flex align-items-center gap-2"
                onClick={() => {
                  setInactivePastExpanded((e) => !e);
                }}
                aria-expanded={showInactivePastChips}
                aria-controls="inactive-past-appointments-chips"
                id="inactive-past-appointments-toggle"
              >
                {showInactivePastChips ? (
                  <ChevronDown size={14} className="flex-shrink-0" aria-hidden />
                ) : (
                  <ChevronRight size={14} className="flex-shrink-0" aria-hidden />
                )}
                <span
                  className="badge rounded-pill px-2 py-1 border small"
                  style={{
                    background: '#e9ecef',
                    color: '#495057',
                    borderColor: '#ced4da',
                  }}
                >
                  {inactiveNavItems.length}
                </span>
                <span className="fw-semibold small text-secondary">past / inactive appointments</span>
              </button>
            )}
          </div>
          {recommendationNavItems.length > 0 && (
            <Collapse in={activeAppointmentsExpanded}>
              <div id="active-appointments-chips">
                <div
                  className="d-flex flex-nowrap gap-1 pb-1"
                  style={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarGutter: 'stable',
                  }}
                  role="radiogroup"
                  aria-label="Active appointments"
                >
                  {recommendationNavItems.map((item) => {
                    const rid = item.recommendation_id;
                    const ridStr = rid != null ? String(rid) : '';
                    const selected =
                      rid != null &&
                      (navPendingRecommendationId != null
                        ? ridStr === navPendingRecommendationId
                        : periopResult?.active !== false &&
                          ridStr === String(periopResult?.recommendation_id ?? ''));
                    const aprv = approvalStatusForNavItem(item);
                    const { proc, datePart } = formatNavChipOneLine(item);
                    const chipAid = item.appointment_id;
                    const canDeactivateChip = chipAid != null && String(chipAid).trim() !== '';
                    const chipShellStyle = getAppointmentNavChipStyle(aprv, selected, isUpdatingRecommendation);
                    return (
                      <div
                        key={rid}
                        role="presentation"
                        className="rounded-2 flex-shrink-0 d-inline-flex align-items-stretch overflow-hidden transition-all"
                        style={{
                          ...chipShellStyle,
                          display: 'inline-flex',
                          alignItems: 'stretch',
                          padding: 0,
                          whiteSpace: 'normal',
                          cursor: 'default',
                        }}
                      >
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={isUpdatingRecommendation}
                          onClick={() => handleRecommendationNavChange(rid)}
                          className="text-start border-0 small"
                          style={{
                            background: 'transparent',
                            cursor: isUpdatingRecommendation ? 'wait' : 'pointer',
                            flex: '0 0 auto',
                            minWidth: 'min-content',
                            padding: '0.25rem 0 0.25rem 0.5rem',
                            lineHeight: 1.25,
                            fontSize: '0.8125rem',
                            wordBreak: 'break-word',
                          }}
                          title={formatRecommendationNavLabel(item)}
                        >
                          <span className="fw-semibold" style={{ color: navChipTitleTextColor(aprv) }}>
                            {proc}
                          </span>
                          {datePart ? (
                            <span className="text-muted">{` · ${datePart}`}</span>
                          ) : null}
                        </button>
                        {canDeactivateChip ? (
                          <button
                            type="button"
                            className="border-0 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              background: 'transparent',
                              borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                              color: 'var(--bs-danger)',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              lineHeight: 1,
                              padding: '0 0.4rem',
                              minWidth: '1.5rem',
                            }}
                            aria-label="Deactivate this appointment"
                            title="Deactivate this appointment"
                            disabled={isUpdatingRecommendation || isDeactivating}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeactivateTargetAppointmentId(String(chipAid).trim());
                              setShowDeactivateModal(true);
                            }}
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Collapse>
          )}
          {inactiveNavItems.length > 0 && (
            <Collapse in={showInactivePastChips}>
              <div id="inactive-past-appointments-chips">
                <div
                  className="d-flex flex-nowrap gap-1 pb-1"
                  style={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarGutter: 'stable',
                  }}
                  role="radiogroup"
                  aria-label="Past inactive appointments"
                >
                  {inactiveNavItems.map((item) => {
                    const rid = item.recommendation_id;
                    const ridStr = rid != null ? String(rid) : '';
                    const selected =
                      rid != null &&
                      (navPendingRecommendationId != null
                        ? ridStr === navPendingRecommendationId
                        : periopResult?.active === false &&
                          ridStr === String(periopResult?.recommendation_id ?? ''));
                    const aprv = approvalStatusForNavItem(item);
                    const { proc, datePart } = formatNavChipOneLine(item);
                    return (
                      <button
                        key={`inactive-${rid}`}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={isUpdatingRecommendation}
                        onClick={() => handleInactiveRecommendationNavChange(rid)}
                        className="text-start border rounded-2 px-2 py-1 small transition-all"
                        style={{
                          ...getAppointmentNavChipStyle(aprv, selected, isUpdatingRecommendation),
                          wordBreak: 'break-word',
                        }}
                        title={formatRecommendationNavLabel(item)}
                      >
                        <span className="fw-semibold" style={{ color: navChipTitleTextColor(aprv) }}>
                          {proc}
                        </span>
                        {datePart ? (
                          <span className="text-muted">{` · ${datePart}`}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Collapse>
          )}
        </div>
      )}
    </>
  );

  const showAppointmentScheduleInHeader =
    !hideAppointmentsForJointDecisions &&
    (appointmentNavLoading ||
      recommendationNavItems.length > 0 ||
      inactiveNavItems.length > 0);

  const belowJdReady = showSurgeryOnly ? !isUpdatingRecommendation : jdStageReady;

  const appointmentsAvailableForHeader =
    appointmentNavLoading ||
    recommendationNavItems.length > 0 ||
    inactiveNavItems.length > 0;

  const handleToggleAppointmentsInHeader = () => {
    setHideAppointmentsForJointDecisions(false);
    setActiveAppointmentsExpanded(true);
  };

  const notesHistoryPanel = (
    <>
      {selectedNotes.size > 0 && (
        <div className="d-flex justify-content-end mb-2">
          <Button variant="danger" size="sm" onClick={handleDeleteSelected}>
            Delete Selected ({selectedNotes.size})
          </Button>
        </div>
      )}
      {isUpdatingRecommendation ? (
        <div className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading notes...</span>
        </div>
      ) : savedNotes.length > 0 ? (
        <Table bordered striped responsive size="sm" className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>Date</th>
              <th>Note</th>
              <th>Added By</th>
              <th>Changes</th>
            </tr>
          </thead>
          <tbody>
            {savedNotes.map((note) => (
              <tr key={note.note_id}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={selectedNotes.has(note.note_id)}
                    onChange={() => handleSelectNote(note.note_id)}
                  />
                </td>
                <td style={{ width: '200px' }}>
                  {new Date(note.created_at).toLocaleString()}
                </td>
                <td>{note.note_text}</td>
                <td>{note.user_email}</td>
                <td>
                  {note.added?.length > 0 && (
                    <div>
                      <strong>Added:</strong> {note.added.length} items
                    </div>
                  )}
                  {note.removed?.length > 0 && (
                    <div>
                      <strong>Removed:</strong> {note.removed.length} items
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted small mb-0">No notes available</p>
      )}
    </>
  );

  return (
    <div className={`patient-tab px-4 pb-4 pt-0 d-flex flex-column${showCompass ? ' patient-tab--compass' : ''}`}>
      <PatientHeader
        patient={patient}
        recommendation={periopResult}
        isLoading={isUpdatingRecommendation}
        compactBottom={showAppointmentScheduleInHeader}
        scheduleSlot={showAppointmentScheduleInHeader ? appointmentScheduleSlot : null}
        appointmentsAvailable={appointmentsAvailableForHeader}
        appointmentsVisible={showAppointmentScheduleInHeader}
        appointmentsCount={
          recommendationNavItems.length > 0
            ? recommendationNavItems.length
            : inactiveNavItems.length
        }
        appointmentsLabel={
          recommendationNavItems.length > 0
            ? 'active appointments'
            : 'past / inactive appointments'
        }
        appointmentsMuted={recommendationNavItems.length === 0 && inactiveNavItems.length > 0}
        onToggleAppointments={handleToggleAppointmentsInHeader}
        showApprovalStatus={showSurgeryOnly}
        listBlurPatientName={blurPatientNames}
        approvalFeedback={approvalFeedback}
        studyWithdrawn={studyWithdrawn}
        withdrawBusy={withdrawBusy}
        withdrawError={withdrawError}
        withdrawStudyName={ENROLLMENT_STUDIES.find((item) => item.id === study)?.name || 'this study'}
        onWithdrawFromStudy={showCompass ? onWithdrawFromStudy : null}
      />
      {showCompass && (
        <div className="compass-patient-stage">
          {study === 'alliance' ? (
            <AllianceWorkspace
              embedded
              demo
              patient={patient}
              onEngage={handleChartEngage}
              handoff={studyHandoff?.studies?.some((item) => item.id === 'alliance') ? studyHandoff : null}
            />
          ) : (
            <CompassWorkspace
              embedded
              demo
              patient={patient}
              onEngage={handleChartEngage}
            />
          )}
        </div>
      )}
      {jointDecisionsMounted && (
      <div
        id="joint_decisions"
        className="mt-2 mb-3"
        style={{
          order: 10,
          display: showSurgeryOnly ? 'none' : undefined,
        }}
        aria-hidden={showSurgeryOnly}
        inert={showSurgeryOnly ? '' : undefined}
      >
        <JointDecisionsSpreadsheet
          key={patient?.id || 'joint-decisions'}
          patient={patient}
          recommendation={periopResult}
          appointmentId={appointmentId}
          onEngage={handleJointDecisionsEngage}
          layoutAnchor="primary"
          onDecideSubmit={handleDecideSubmit}
          onStageReady={setJdStageReady}
          enrollmentSurgeryPrompt={enrollmentSurgeryPrompt}
        />
      </div>
      )}
      {medManagementMounted && (
        <div
          style={{
            order: 20,
            display: showSurgeryOnly ? undefined : 'none',
          }}
          aria-hidden={!showSurgeryOnly}
          inert={!showSurgeryOnly ? '' : undefined}
        >
          <div id="rec_and_text" className="mt-2">
            <PatientSummaryCard 
              patient={patient}
              recommendation={periopResult}
              appointmentId={appointmentId}
              archiveView={periopResult?.active === false}
              messages={messages}
              setMessages={setMessages}
              isLoading={isUpdatingRecommendation}
              isUpdatingRecommendation={isUpdatingRecommendation}
              refreshTrigger={refreshMessages}
              onApprove={handleRecommendationApproval}
            />
          </div>
            
          <div id="overview">
            {/* TOC anchor: Added Notes */}
            <div id="notes" style={{ scrollMarginTop: '0.75rem' }} aria-hidden />
            <MedicationCheckboxForm
              ref={medicationFormRef}
              periopResult={periopResult}
              onSave={handleMedicationFormSave}
              resetTrigger={resetFormTrigger}
              notesHistoryPanel={notesHistoryPanel}
            />
            <div className="d-flex justify-content-end mb-3">
              <Button 
                variant={hasPendingChanges ? "danger" : "primary"}
                onClick={() => medicationFormRef.current && medicationFormRef.current.handleSave && medicationFormRef.current.handleSave()}
                disabled={isUpdatingRecommendation}
                style={hasPendingChanges ? {
                  animation: 'pulse 2s infinite',
                  boxShadow: '0 0 10px rgba(220, 53, 69, 0.5)'
                } : {}}
              >
                {isUpdatingRecommendation ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3" style={{ order: 30 }}>
        {saveError && <div className="text-danger">{saveError}</div>}
      </div>

      {/* Patient Details Section — hold until Joint Decisions (or MM rec) has settled */}
      <div
        id="details"
        className="mb-3"
        style={{
          order: 50,
          minHeight: belowJdReady ? undefined : '14rem',
        }}
        aria-busy={!belowJdReady}
      >
        {belowJdReady ? ( 
          <Card>
            <Card.Body>
              {/* Summary Section */}
              
                {patient.summary?.content && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>Summary</Card.Title>
                      <ReactMarkdown>{patient.summary.content}</ReactMarkdown>
                    </Card.Body>
                  </Card>
                )}
              

              {/* Conditions */}
                {(() => {
                  const conditions = periopResult?.inputs?.filter(item => item.condition && !item.date) || [];
                  console.log('filtered conditions:', conditions);
                  
                  return conditions.length > 0;
                })() && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>Conditions</Card.Title>
                      <Table bordered striped>
                        <thead>
                          <tr>
                            <th>Condition</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periopResult.inputs
                            .filter(item => item.condition && !item.date)
                            .sort((a, b) => a.condition.localeCompare(b.condition))
                            .map((condition, index) => (
                            <tr key={index}>
                              <td>{condition.condition}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              

              {/* Medications */}
              
                {(() => {
                  // Debug logging
                  console.log('periopResult:', periopResult);
                  console.log('periopResult?.inputs:', periopResult?.inputs);
                  const medications = periopResult?.inputs?.filter(item => item.medication) || [];
                  console.log('filtered medications:', medications);
                  
                  return medications.length > 0;
                })() && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>Medications</Card.Title>
                      <Table bordered striped>
                        <thead>
                          <tr>
                            <th>Medication</th>
                            <th>Dosage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {periopResult.inputs
                            .filter(item => item.medication)
                            .sort((a, b) => a.medication.localeCompare(b.medication))
                            .map((medication, index) => (
                            <tr key={index}>
                              <td>{medication.medication}</td>
                              <td>{medication.dosage || 'Not specified'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              

              {/* EMR Source */}
              {patient.emr_source && (
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>EMR Source</Card.Title>
                    <p><strong>Patient ID:</strong> {patient.emr_source.patient_id}</p>
                    <p><strong>System ID:</strong> {patient.emr_source.system_id}</p>
                  </Card.Body>
                </Card>
              )}

              
              {/* Events */}
                {(() => {
                  const events = periopResult?.inputs?.filter(item => item.event && item.date) || [];
                  console.log('filtered events:', events);
                  
                  return events.length > 0;
                })() && (
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>Events</Card.Title>
                      <Table bordered striped>
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Date</th>
                            
                          </tr>
                        </thead>
                        <tbody>
                          {periopResult.inputs
                            .filter(item => item.event && item.date)
                            .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
                            .map((event, index) => (
                            <tr key={index}>
                              <td>{event.event}</td>
                              <td>{new Date(event.date).toLocaleDateString()}</td>
                              
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
            </Card.Body>
          </Card>
        ) : null}
      </div>

      <div style={{ order: 60 }}>
      <Modal
        show={showDeactivateModal}
        onHide={() => {
          if (!isDeactivating) {
            setShowDeactivateModal(false);
            setDeactivateTargetAppointmentId(null);
          }
        }}
        centered
      >
        <Modal.Header closeButton={!isDeactivating}>
          <Modal.Title>Deactivate appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            This will mark <strong>every recommendation</strong> for this appointment ID as{' '}
            <strong>inactive</strong> (same as archived / past appointments). Another active appointment
            will load automatically if one exists.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeactivateModal(false);
              setDeactivateTargetAppointmentId(null);
            }}
            disabled={isDeactivating}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeactivateAppointmentConfirm} disabled={isDeactivating}>
            {isDeactivating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deactivating…
              </>
            ) : (
              'Confirm deactivate'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
      </div>

  );
}

export default PatientTab;
